import { z } from 'zod';
import * as dt from './data.types.js';
import { OCR_RESOLUTION_METHOD } from './data.types.js';

const gatewaySerialNumberParamSchema = z.string().min(1).max(255).trim();
const containerSlotSchema = z.coerce.number().int().min(1).max(25);
const integerIdSchema = z.coerce.number().int().positive();
const BaseResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
});

export const containerSlotScanJobPhaseSchema = z.nativeEnum(dt.CONTAINER_SLOT_SCAN_JOB_PHASE);
export const containerSlotScanSlotPhaseSchema = z.nativeEnum(dt.CONTAINER_SLOT_SCAN_SLOT_PHASE);
export const containerSlotScanSlotOutcomeSchema = z.nativeEnum(dt.CONTAINER_SLOT_SCAN_SLOT_OUTCOME);
export const ocrPhaseSchema = z.nativeEnum(dt.CONTAINER_SLOT_SCAN_OCR_PHASE);

export const containerSlotScanJobOptionsSchema = z.object({
  autoOcr: z.boolean().default(true),
  maxOcrRetries: z.number().int().min(0).max(5).default(2),
  inferenceProvider: z.string().optional(),
  sagemakerEndpointName: z.string().optional(),
  sagemakerTask: z.enum(['ocr', 'extract']).optional(),
  autoApplyFingerprint: z.boolean().optional(),
  ocrContext: z.string().optional(),
});


export const initiateContainerSlotScanJobRequestSchema = z.object({
  gatewaySerialNumber: gatewaySerialNumberParamSchema,
  loadPortId: z.number().int().min(1),
  slotNumbers: z.array(containerSlotSchema).min(1),
  waferImagingTemplateId: z.number().int().positive(),
  lightingSource: z.enum(['template_starting', 'wafer_learned']).default('template_starting'),
  waferId: integerIdSchema.optional(),
  containerId: integerIdSchema.optional(),
  options: containerSlotScanJobOptionsSchema.optional(),
});


export const resolvedFingerprintSchema = z.object({
  value: z.string(),
  confidence: z.number().min(0).max(1),
  resolutionMethod: z.nativeEnum(OCR_RESOLUTION_METHOD),
  requiredReview: z.boolean(),
  explanation: z.string(),
});


export const containerSlotScanAuditSummarySchema = z.object({
  overall: z.enum(['pass', 'fail', 'partial']),
  slots: z.array(
    z.object({
      slotNumber: containerSlotSchema,
      expected: z.string(),
      actual: z.string().optional(),
      match: z.boolean(),
      confidence: z.number().min(0).max(1).optional(),
    }),
  ),
});


export const containerSlotScanJobProgressSchema = z.object({
  jobId: z.string(),
  phase: containerSlotScanJobPhaseSchema,
  totalSlots: z.number().int(),
  completedSlots: z.number().int(),
  slots: z.array(
    z.object({
      slotNumber: containerSlotSchema,
      containerSlotScanPhase: containerSlotScanSlotPhaseSchema,
      outcome: containerSlotScanSlotOutcomeSchema.optional(),
      errorMessage: z.string().optional(),
      images: z
        .array(
          z.object({
            imageIndex: z.number().int().min(0),
            ocrPhase: ocrPhaseSchema,
            fingerprint: z.string().optional(),
            confidence: z.number().min(0).max(1).optional(),
            objectKey: z.string().optional(),
            ocrErrorMessage: z.string().optional(),
          }),
        )
        .optional(),
      resolvedFingerprint: resolvedFingerprintSchema.optional(),
      userFingerprintOverride: z.string().nullable().optional(),
      effectiveFingerprint: z.string().nullable().optional(),
    }),
  ),
  summary: z.object({
    successful: z.number().int(),
    failed: z.number().int(),
    noWafer: z.number().int(),
    ocrPending: z.number().int(),
  }),
  timing: z.object({
    startedAt: z.string().optional(),
    finishedAt: z.string().optional(),
  }),
  audit: containerSlotScanAuditSummarySchema.optional(),
  containerId: z.number().int().positive().nullable().optional(),
  /** Derived from load port size captured on the job at scan initiation (not container DB lookup). */
  containerSize: z.nativeEnum(dt.CONTAINER_SIZE).nullable().optional(),
  loadPortSize: z.nativeEnum(dt.EFEM_LOAD_PORT_SIZE).nullable().optional(),
});


export { tenantJobIdParamSchema as containerSlotScanJobIdParamSchema } from './tenant-job-id.schemas.js';

export const initiateContainerSlotScanJobResponseSchema = BaseResponseSchema.extend({
  data: z.object({ jobId: z.string() }),
});

export const containerSlotScanActiveJobSchema = z.object({
  jobId: z.string(),
  phase: containerSlotScanJobPhaseSchema,
  gatewaySerialNumber: z.string().nullable(),
  efemClientName: z.string().nullable(),
  loadPortId: z.number().int().min(1),
  containerId: integerIdSchema.nullable(),
  slotNumbers: z.array(containerSlotSchema),
  totalSlots: z.number().int().min(0),
  completedSlots: z.number().int().min(0),
  phaseError: z.string().nullable().optional(),
  initiatedByUserId: integerIdSchema.nullable(),
  initiatedByEmail: z.string().nullable().optional(),
  startedAt: z.string().optional(),
  finishedAt: z.string().nullable().optional(),
  updatedAt: z.string(),
});

export const containerSlotScanConflictDetailsSchema = z.object({
  existingJobId: z.string(),
  phase: containerSlotScanJobPhaseSchema,
});

export const containerSlotScanConflictResponseSchema = BaseResponseSchema.extend({
  errorCode: z.literal('CONTAINER_SLOT_SCAN_CONFLICT').optional(),
  details: containerSlotScanConflictDetailsSchema,
});


export const containerSlotScanJobProgressResponseSchema = BaseResponseSchema.extend({
  data: containerSlotScanJobProgressSchema,
});

export const retryContainerSlotScanOcrRequestSchema = z.object({
  slotNumbers: z.array(containerSlotSchema).min(1).optional(),
});


export const retryContainerSlotScanOcrQuerySchema = z.object({
  force: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .optional()
    .transform((v) => v === true || v === 'true'),
});

export const applyContainerSlotScanBatchInputResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    containerId: integerIdSchema,
    waferIds: z.array(integerIdSchema),
    createdWaferCount: z.number().int().min(0),
  }),
});

export const containerSlotScanStuckReasonSchema = z.enum([
  'scanning_idle',
  'ocr_idle',
  'never_started',
]);

export const containerSlotScanStuckJobSchema = z.object({
  jobId: z.string(),
  tenantId: z.string().uuid(),
  phase: containerSlotScanJobPhaseSchema,
  loadPortId: z.number().int().min(1),
  containerId: integerIdSchema.nullable(),
  stuckReason: containerSlotScanStuckReasonSchema,
  lastSlotActivityAt: z.string().nullable(),
  containerSlotScanningStartedAt: z.string().nullable(),
  ocrStartedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});


export const applyPendingFingerprintRequestSchema = z.object({
  scanJobId: z.string().optional(),
});

export const containerSlotScanTelemetryEventSchema = z.object({
  type: z.literal('slot_progress'),
  tenantId: z.string().uuid(),
  jobId: z.string(),
  slotNumber: z.number().int().positive(),
  status: z.string(),
  /** Monotonic per (jobId, slotNumber); Phase 4b ordering. */
  eventSeq: z.number().int().positive().optional(),
  result: z.unknown().optional(),
  timestamp: z.string(),
});


export const waferIdInferenceProviderIdSchema = z.enum([
  'dev_mock',
  'rekognition',
  'textract',
  'sagemaker',
]);


export const waferIdSagemakerTaskSchema = z.enum(['ocr', 'extract']);


const containerSlotScanOcrQueueMessageCommonSchema = {
  tenantId: z.string().uuid(),
  imageId: z.string().uuid(),
  objectKey: z.string(),
  attempt: z.number().int().min(0).default(0),
  inferenceProvider: waferIdInferenceProviderIdSchema.optional(),
  /** Overrides CONTAINER_SLOT_SCAN_SAGEMAKER_ENDPOINT_NAME when provider is sagemaker. */
  sagemakerEndpointName: z.string().min(1).optional(),
  sagemakerTask: waferIdSagemakerTaskSchema.optional(),
};

export const containerSlotScanOcrQueueMessageSchema = z.discriminatedUnion('target', [
  z.object({
    target: z.literal('container_slot_scan_job_image'),
    jobId: z.string(),
    slotNumber: z.number().int().positive(),
    ...containerSlotScanOcrQueueMessageCommonSchema,
  }),
  z.object({
    target: z.literal('inference_batch_image'),
    batchId: z.string().uuid(),
    ...containerSlotScanOcrQueueMessageCommonSchema,
  }),
]);


export const waferImageInferenceResultSchema = z.object({
  fingerprint: z.object({
    text: z.string(),
    confidence: z.number().min(0).max(100),
  }),
  blurScore: z.number().min(0).max(100).optional(),
  provider: z.string().optional(),
  modelId: z.string().optional(),
});


/** Invoke Lambda → result-handler queue (inference outcome + original work message). */
export const containerSlotScanOcrResultEnvelopeSchema = z
  .object({
    work: containerSlotScanOcrQueueMessageSchema,
    inference: waferImageInferenceResultSchema.optional(),
    error: z.string().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.inference != null) {
      return;
    }
    if (!val.error?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'error is required when inference is absent',
        path: ['error'],
      });
    }
  });


export const sageMakerWaferImageBatchRequestItemSchema = z.object({
  id: z.string().min(1),
  s3Uri: z.string().min(1),
});


export const sageMakerWaferImageBatchResultItemSchema = z
  .object({
    id: z.string().min(1),
    text: z.string().optional(),
    confidence: z.number().optional(),
    error: z.string().optional(),
  })
  .superRefine((val, ctx) => {
    const hasText = val.text != null && val.text.trim().length > 0;
    const hasError = val.error != null && val.error.trim().length > 0;
    if (hasText && hasError) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'each batch result requires text or error, not both',
      });
    }
  });


export const sageMakerWaferImageBatchInferenceResponseSchema = z.object({
  results: z.array(sageMakerWaferImageBatchResultItemSchema),
  modelType: z.string().optional(),
  processingTime: z.number().optional(),
  batchSize: z.number().int().positive().optional(),
});


/** Edge MQTT payload for container_slot_inspection_progress */
export const containerSlotInspectionProgressSchema = z.object({
  jobId: z.string(),
  slotNumber: z.number().int().positive(),
  status: z.string(),
  eventSeq: z.number().int().positive().optional(),
  result: z
    .object({
      capturedImages: z.array(z.unknown()).optional(),
      error: z.string().optional(),
    })
    .optional(),
  timestamp: z.string(),
});
// Inferred types
export type ContainerSlotScanActiveJob = z.infer<typeof containerSlotScanActiveJobSchema>;
export type ContainerSlotScanAuditSummary = z.infer<typeof containerSlotScanAuditSummarySchema>;
export type ContainerSlotScanJobOptions = z.infer<typeof containerSlotScanJobOptionsSchema>;
export type ContainerSlotScanJobProgress = z.infer<typeof containerSlotScanJobProgressSchema>;
export type ContainerSlotScanOcrQueueMessage = z.infer<typeof containerSlotScanOcrQueueMessageSchema>;
export type ContainerSlotScanOcrResultEnvelope = z.infer<
  typeof containerSlotScanOcrResultEnvelopeSchema
>;
export type ContainerSlotScanStuckJob = z.infer<typeof containerSlotScanStuckJobSchema>;
export type ContainerSlotScanTelemetryEvent = z.infer<typeof containerSlotScanTelemetryEventSchema>;
export type InitiateContainerSlotScanJobRequest = z.infer<
  typeof initiateContainerSlotScanJobRequestSchema
>;
export type ResolvedFingerprint = z.infer<typeof resolvedFingerprintSchema>;
export type RetryContainerSlotScanOcrRequest = z.infer<
  typeof retryContainerSlotScanOcrRequestSchema
>;
export type SageMakerWaferImageBatchInferenceResponse = z.infer<
  typeof sageMakerWaferImageBatchInferenceResponseSchema
>;
export type SageMakerWaferImageBatchRequestItem = z.infer<
  typeof sageMakerWaferImageBatchRequestItemSchema
>;
export type SageMakerWaferImageBatchResultItem = z.infer<
  typeof sageMakerWaferImageBatchResultItemSchema
>;
export type WaferIdInferenceProviderId = z.infer<typeof waferIdInferenceProviderIdSchema>;
export type WaferIdSagemakerTask = z.infer<typeof waferIdSagemakerTaskSchema>;
export type WaferImageInferenceResult = z.infer<typeof waferImageInferenceResultSchema>;
